import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, Grid } from 'lucide-react';
import api from '../utils/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  
  // Estado para navegación contextual
  const [contextProducts, setContextProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [navigationContext, setNavigationContext] = useState({
    type: 'catalog', // 'catalog' o 'category'
    category: null,
    categoryName: null
  });

  // Detectar contexto de navegación desde URL o state
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromCategory = searchParams.get('from') || location.state?.fromCategory;
    const categoryName = searchParams.get('categoryName') || location.state?.categoryName;
    
    if (fromCategory) {
      setNavigationContext({
        type: 'category',
        category: fromCategory,
        categoryName: categoryName || fromCategory
      });
    } else {
      setNavigationContext({
        type: 'catalog',
        category: null,
        categoryName: null
      });
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setSelectedImageIndex(0);
      try {
        const [productRes, configRes] = await Promise.all([
          api.get(`/products/${id}`),
          api.get('/config')
        ]);
        setProduct(productRes.data);
        setConfig(configRes.data);
        
        // Cargar productos del contexto para navegación
        let contextUrl = '/products?limit=100';
        if (navigationContext.type === 'category' && navigationContext.category) {
          contextUrl = `/products?category=${navigationContext.category}&limit=100`;
        }
        
        const contextRes = await api.get(contextUrl);
        setContextProducts(contextRes.data);
        
        // Encontrar índice actual
        const idx = contextRes.data.findIndex(p => p.id === id);
        setCurrentIndex(idx);
        
        // Cargar productos relacionados (de la misma categoría)
        const relatedRes = await api.get(`/products?category=${productRes.data.category}&limit=4`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== id));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigationContext]);

  // Navegación entre productos - declarar funciones antes de cualquier return condicional
  const getPrevProduct = useCallback(() => {
    if (currentIndex > 0) {
      return contextProducts[currentIndex - 1];
    }
    return null;
  }, [currentIndex, contextProducts]);

  const getNextProduct = useCallback(() => {
    if (currentIndex < contextProducts.length - 1 && currentIndex >= 0) {
      return contextProducts[currentIndex + 1];
    }
    return null;
  }, [currentIndex, contextProducts]);

  const navigateToProduct = useCallback((productId) => {
    const params = new URLSearchParams();
    if (navigationContext.type === 'category' && navigationContext.category) {
      params.set('from', navigationContext.category);
      if (navigationContext.categoryName) {
        params.set('categoryName', navigationContext.categoryName);
      }
    }
    const queryString = params.toString();
    navigate(`/product/${productId}${queryString ? `?${queryString}` : ''}`);
  }, [navigationContext, navigate]);

  const getBackUrl = useCallback(() => {
    if (navigationContext.type === 'category' && navigationContext.category) {
      return `/catalog?category=${encodeURIComponent(navigationContext.category)}`;
    }
    return '/catalog';
  }, [navigationContext]);

  const getBackLabel = useCallback(() => {
    if (navigationContext.type === 'category' && navigationContext.categoryName) {
      return `Volver a ${navigationContext.categoryName}`;
    }
    return 'Volver a Productos';
  }, [navigationContext]);

  // Funciones para navegación de imágenes
  const goToPrevImage = useCallback(() => {
    if (product && product.images && product.images.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  }, [product]);

  const goToNextImage = useCallback(() => {
    if (product && product.images && product.images.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  }, [product]);

  const prevProduct = getPrevProduct();
  const nextProduct = getNextProduct();

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignorar si se está escribiendo en un input o si el producto no está cargado
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || !product) return;

      switch (e.key) {
        case 'ArrowLeft':
          if (e.shiftKey) {
            goToPrevImage();
          } else if (prevProduct) {
            navigateToProduct(prevProduct.id);
          }
          break;
        case 'ArrowRight':
          if (e.shiftKey) {
            goToNextImage();
          } else if (nextProduct) {
            navigateToProduct(nextProduct.id);
          }
          break;
        case 'Escape':
          navigate(getBackUrl());
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevProduct, nextProduct, goToPrevImage, goToNextImage, navigate, navigateToProduct, getBackUrl, product]);

  // Swipe en móvil: izquierda → siguiente producto, derecha → anterior
  const touchStartRef = React.useRef({ x: 0, y: 0, time: 0 });

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    // Solo horizontal, rápido, distancia mínima
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 500) {
      if (dx < 0 && nextProduct) {
        navigateToProduct(nextProduct.id);
      } else if (dx > 0 && prevProduct) {
        navigateToProduct(prevProduct.id);
      }
    }
  };

  // Returns condicionales después de todos los hooks
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-white/60">Cargando...</p></div>;
  }

  if (!product) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-white/60">Producto no encontrado</p></div>;
  }

  const handleWhatsApp = () => {
    if (config?.whatsapp_number) {
      const message = `Hola, estoy interesado en: ${product.name} (${product.sku})`;
      const url = `https://wa.me/${config.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div
      className="px-4 md:px-12 lg:px-24 py-12"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="product-detail-wrapper"
    >
      {/* Barra de Navegación Superior - Premium */}
      <nav 
        className="mb-8 bg-gradient-to-r from-[#0A0A0A] via-[#111111] to-[#0A0A0A] border border-white/10 p-4"
        data-testid="product-navigation-bar"
      >
        <div className="flex items-center justify-between">
          {/* Producto Anterior */}
          <div className="flex-1">
            {prevProduct ? (
              <button
                onClick={() => navigateToProduct(prevProduct.id)}
                data-testid="nav-prev-product"
                className="group flex items-center gap-3 text-white/60 hover:text-white transition-all duration-300"
              >
                <div className="w-10 h-10 border border-white/20 group-hover:border-white/50 flex items-center justify-center transition-all duration-300 group-hover:bg-white/5">
                  <ArrowLeft className="w-4 h-4" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 group-hover:text-white/60">Anterior</p>
                  <p className="text-xs truncate max-w-[120px] md:max-w-[180px]">{prevProduct.name}</p>
                </div>
              </button>
            ) : (
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center opacity-30">
                <ArrowLeft className="w-4 h-4" />
              </div>
            )}
          </div>

          {/* Volver al Listado - Centro */}
          <div className="flex-shrink-0 px-4">
            <Link
              to={getBackUrl()}
              data-testid="nav-back-to-list"
              className="group flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/20 hover:bg-white hover:text-black transition-all duration-300"
            >
              <Grid className="w-4 h-4" />
              <span className="text-xs uppercase tracking-[0.15em] font-medium hidden sm:inline">
                {getBackLabel()}
              </span>
              <span className="text-xs uppercase tracking-[0.15em] font-medium sm:hidden">
                Volver
              </span>
            </Link>
          </div>

          {/* Producto Siguiente */}
          <div className="flex-1 flex justify-end">
            {nextProduct ? (
              <button
                onClick={() => navigateToProduct(nextProduct.id)}
                data-testid="nav-next-product"
                className="group flex items-center gap-3 text-white/60 hover:text-white transition-all duration-300"
              >
                <div className="hidden sm:block text-right">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 group-hover:text-white/60">Siguiente</p>
                  <p className="text-xs truncate max-w-[120px] md:max-w-[180px]">{nextProduct.name}</p>
                </div>
                <div className="w-10 h-10 border border-white/20 group-hover:border-white/50 flex items-center justify-center transition-all duration-300 group-hover:bg-white/5">
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            ) : (
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center opacity-30">
                <ArrowRight className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>

        {/* Indicador de posición */}
        {contextProducts.length > 0 && currentIndex >= 0 && (
          <div className="mt-3 pt-3 border-t border-white/5 flex justify-center">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/30">
              Producto {currentIndex + 1} de {contextProducts.length}
              {navigationContext.type === 'category' && navigationContext.categoryName && (
                <span className="ml-2 text-white/40">en {navigationContext.categoryName}</span>
              )}
            </p>
          </div>
        )}
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-testid="product-detail">
        <div>
          {/* Contenedor de imagen principal con flechas de navegación */}
          <div className="relative aspect-[4/5] bg-white border border-gray-100 mb-4 overflow-hidden group">
            <img
              src={product.images[selectedImageIndex] || product.images[0]}
              alt={product.name}
              className="w-full h-full object-contain p-4"
            />
            
            {/* Flechas de navegación de imágenes */}
            {product.images.length > 1 && (
              <>
                {/* Flecha Izquierda */}
                <button
                  onClick={goToPrevImage}
                  data-testid="image-nav-prev"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                
                {/* Flecha Derecha */}
                <button
                  onClick={goToNextImage}
                  data-testid="image-nav-next"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
                  aria-label="Imagen siguiente"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                {/* Indicador de imagen (puntos) */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {product.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        selectedImageIndex === idx 
                          ? 'bg-white w-4' 
                          : 'bg-white/40 hover:bg-white/70'
                      }`}
                      aria-label={`Ver imagen ${idx + 1}`}
                    />
                  ))}
                </div>
                
                {/* Contador de imágenes */}
                <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 font-medium">
                  {selectedImageIndex + 1} / {product.images.length}
                </div>
              </>
            )}
          </div>
          
          {/* Thumbnails */}
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  data-testid={`thumbnail-${idx}`}
                  className={`aspect-square bg-white overflow-hidden border-2 ${selectedImageIndex === idx ? 'border-black' : 'border-gray-200'} transition-all duration-300 hover:border-gray-400`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
          
          {/* Instrucciones de teclado */}
          <div className="mt-4 text-center">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/30">
              <span className="hidden md:inline">
                ← → Cambiar producto • Shift + ← → Cambiar imagen • Esc Volver
              </span>
              <span className="md:hidden">
                Desliza o toca las flechas
              </span>
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-3">{product.brand}</p>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl tracking-tighter mb-6">{product.name}</h1>
          
          {config?.show_prices !== false ? (
            <div className="flex items-center gap-4 mb-8">
              {product.promotional_price ? (
                <>
                  <p className="text-2xl font-semibold">${product.promotional_price.toLocaleString()}</p>
                  <p className="text-lg text-white/40 line-through">${product.price.toLocaleString()}</p>
                </>
              ) : (
                <p className="text-2xl font-semibold">${product.price.toLocaleString()}</p>
              )}
            </div>
          ) : (
            <div className="mb-8">
              <p className="text-lg text-white/50 italic">Consultar precio por WhatsApp</p>
            </div>
          )}

          <div className="mb-8">
            <p className="text-sm text-white/80 leading-relaxed">{product.short_description}</p>
          </div>

          {product.sizes.length > 0 && (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.2em] mb-3 font-semibold">Tallas Disponibles</p>
              <div className="flex gap-2">
                {product.sizes.map((size) => (
                  <div key={size} className="border border-white/20 px-4 py-2 text-xs">{size}</div>
                ))}
              </div>
            </div>
          )}

          {product.colors.length > 0 && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] mb-3 font-semibold">Colores</p>
              <p className="text-sm text-white/80">{product.colors.join(', ')}</p>
            </div>
          )}

          {product.cantidad && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] mb-3 font-semibold">Cantidad</p>
              <p className="text-sm text-white/80">{product.cantidad}</p>
            </div>
          )}

          {config?.whatsapp_number && (
            <button
              onClick={handleWhatsApp}
              data-testid="contact-whatsapp-button"
              className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 rounded-none px-10 py-5 uppercase tracking-widest text-xs font-semibold mb-6"
            >
              Contactar por WhatsApp
            </button>
          )}

          <div className="border-t border-white/10 pt-8">
            <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Descripción</h3>
            <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{product.full_description}</p>
          </div>

          {product.material && (
            <div className="border-t border-white/10 pt-8 mt-8">
              <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Material</h3>
              <p className="text-sm text-white/80">{product.material}</p>
            </div>
          )}

          <div className="border-t border-white/10 pt-8 mt-8">
            <p className="text-xs text-white/40">SKU: {product.sku}</p>
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 && null /* Sección "Productos Relacionados" eliminada por solicitud del usuario para dar protagonismo al producto actual */}
    </div>
  );
};

export default ProductDetail;
